import * as THREE from 'three';

// ============================================================
//  Небо: купол с градиентом, диском солнца и облаками.
//  Лист дерева импортов — тянет только three, поэтому его
//  безопасно импортирует scene.js (см. md/code-map.md).
// ============================================================
//
// ⚠️ ГЛАВНОЕ ОГРАНИЧЕНИЕ ЗДЕСЬ — FILL RATE, а не красота. Кадр в этой игре
// упирается в пиксели (баг-урок «FPS упал с 60 до 20» в md/bug-lessons.md),
// а небо — это потенциально пол-экрана. Отсюда три решения, которые выглядят
// как упрощения, но приняты осознанно:
//
//   1. Облака НЕ считаются шумом в шейдере. Шум (FBM) стоил бы десятков
//      инструкций на каждый пиксель неба. Вместо этого шум считается ОДИН РАЗ
//      на старте в JS и кладётся в текстуру 512×512; в шейдере остаётся одна
//      выборка. Освещённость облака солнцем тоже ЗАПЕЧЕНА в текстуру (канал G):
//      солнце у нас неподвижно, значит и подсветка кромки не меняется.
//   2. Купол рисуется ПОСЛЕДНИМ среди непрозрачных (`renderOrder`) с включённым
//      depth-тестом и выключенной записью глубины. Тогда пиксели, закрытые
//      картой, отсекаются тестом и небо шейдится только там, где оно реально
//      видно. Нарисуй мы его первым (как обычный фон) — платили бы за целый
//      экран неба под каждой стеной.
//   3. Облака — не объёмные и не отдельные меши, а ПРОЕКЦИЯ на воображаемый
//      горизонтальный слой над головой. Пешеход не может подойти к облаку и
//      обойти его, поэтому параллакс слоя не нужен, а перспектива (облака
//      сжимаются к горизонту) получается сама собой из деления на d.y.
//
// ⚠️ Купол каждый кадр переносится в точку камеры (`onBeforeRender`). Без этого
// при радиусе 300 и арене 140×140 солнце заметно уезжало бы по небу при беге
// через карту, а дальний край купола вылезал бы за `camera.far`.

/** Сторона текстуры облаков. 512 — компромисс: 2048 генерировался бы
 *  почти секунду на старте, 256 виден блоками на скользящем взгляде. */
const TEX = 512;
/** Октав шума. Каждая — отдельная решётка вдвое мельче предыдущей. */
const OCTAVES = 5;
/** Решётка самой крупной октавы (размер «облачных полей» в тайле). */
const BASE = 4;

// --- Константы вида (крутить их, а не шейдер) ---
/** Порог облачности: БОЛЬШЕ — облаков МЕНЬШЕ (0.45 — сплошная пелена,
 *  0.60 — редкие клочья). */
const COVER = 0.52;
/** Мягкость края облака. Мало — облака как вырезанные из бумаги. */
const SOFT = 0.26;
/** Разброс порога по небу (самой крупной октавой шума). ⚠️ Не украшение:
 *  с одним порогом на весь тайл небо получается равномерно засыпанным
 *  одинаковым «попкорном» — сразу видно, что оно ненастоящее. С разбросом
 *  появляются то плотные гряды, то чистые прогалины. */
const COVER_VAR = 0.20;
/** Насколько далеко (в пикселях текстуры) смотрим в сторону солнца, считая
 *  запечённую подсветку. Это и есть «толщина» облака на глаз. */
const LIT_OFFSET = 5;
/** Масштаб проекции слоя облаков: больше — облака мельче и их больше в кадре. */
const CLOUD_SCALE = 0.3;
/** Ветер: смещение слоя облаков в долях тайла за секунду. Медленно намеренно —
 *  быстрое небо в шутере читается как «что-то мигает на периферии». */
const WIND = [0.0022, 0.0009];
/** Угловой радиус диска солнца, косинусы внутреннего и внешнего края.
 *  ~1.2° — примерно вчетверо крупнее настоящего: настоящее солнце на экране
 *  1920 шириной заняло бы 9 пикселей и читалось бы как битый пиксель. */
const DISK_IN = 0.99987, DISK_OUT = 0.99977;
/** Яркость ядра солнца В ЛИНЕЙНОМ HDR. ⚠️ Больше 1.0 намеренно: порог блума
 *  в scene.js равен 1.0, и только за счёт этого солнце цветёт. С выключенным
 *  блумом (клавиша B) значение просто клампится в белый диск. */
const DISK_GAIN = 7.0;

/** Детерминированный ГПСЧ (mulberry32). ⚠️ Именно детерминированный, а не
 *  Math.random: облака должны быть одинаковыми во всех сессиях, иначе жалоба
 *  «в этом углу небо выглядит иначе» не воспроизводится. */
function mulberry32(a) {
  return function () {
    a = a + 0x6D2B79F5 | 0;
    let t = Math.imul(a ^ a >>> 15, 1 | a);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

/** Билинейная выборка решётки шума со сглаживанием (smoothstep) и ЗАВОРОТОМ
 *  по обеим осям — тайл обязан стыковаться сам с собой, иначе на небе видно
 *  прямой шов, ползущий по ветру. */
function latticeAt(a, n, x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  const x0 = ((xi % n) + n) % n, x1 = (x0 + 1) % n;
  const y0 = ((yi % n) + n) % n, y1 = (y0 + 1) % n;
  const r0 = y0 * n, r1 = y1 * n;
  const top = a[r0 + x0] * (1 - u) + a[r0 + x1] * u;
  const bot = a[r1 + x0] * (1 - u) + a[r1 + x1] * u;
  return top * (1 - v) + bot * v;
}

function smoothstep(e0, e1, x) {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/**
 * Текстура облаков: R — плотность (она же прозрачность), G — освещённость
 * (запечённая подсветка со стороны солнца).
 *
 * ⚠️ Считается на старте ~40–60 мс. Это заметная, но разовая цена; вынести её
 * в воркер можно, но тогда небо появлялось бы через кадр после входа в игру.
 */
function makeCloudTexture(sunDir) {
  const rnd = mulberry32(0x5EED);
  const oct = [];
  let ampSum = 0;
  for (let o = 0; o < OCTAVES; o++) {
    const n = BASE << o, a = new Float32Array(n * n);
    for (let i = 0; i < a.length; i++) a[i] = rnd();
    const amp = 0.5 ** o;
    ampSum += amp;
    oct.push({ n, a, amp });
  }

  // Решётка «где сегодня облачно, а где чисто» — отдельная и намеренно грубая.
  // ⚠️ Обходится РОВНО целое число раз за тайл (n клеток на тайл), иначе на
  // стыке тайла значения не сойдутся и по небу поползёт прямой шов.
  const BIAS_N = 3, biasGrid = new Float32Array(BIAS_N * BIAS_N);
  for (let i = 0; i < biasGrid.length; i++) biasGrid[i] = rnd();

  // 1) плотность. Порог плавает по небу — см. COVER_VAR.
  const dens = new Float32Array(TEX * TEX);
  for (let y = 0; y < TEX; y++) {
    for (let x = 0; x < TEX; x++) {
      let f = 0;
      for (let o = 0; o < OCTAVES; o++) {
        const L = oct[o];
        f += L.amp * latticeAt(L.a, L.n, x / TEX * L.n, y / TEX * L.n);
      }
      const bias = latticeAt(biasGrid, BIAS_N, x / TEX * BIAS_N, y / TEX * BIAS_N) - 0.5;
      const thr = COVER + bias * COVER_VAR;
      dens[y * TEX + x] = smoothstep(thr, thr + SOFT, f / ampSum);
    }
  }

  // 2) освещённость: смотрим на два шага В СТОРОНУ СОЛНЦА по слою облаков.
  //    Пусто там — значит нас солнце достаёт (яркая кромка), плотно — мы в
  //    собственной тени облака. Направление берём из проекции солнца на
  //    горизонталь: слой облаков плоский, вертикаль в нём смысла не имеет.
  const sl = Math.hypot(sunDir.x, sunDir.z) || 1;
  const sx = sunDir.x / sl, sz = sunDir.z / sl;
  const px = (x, y) => dens[(((y % TEX) + TEX) % TEX) * TEX + (((x % TEX) + TEX) % TEX)];
  const data = new Uint8Array(TEX * TEX * 4);
  for (let y = 0; y < TEX; y++) {
    for (let x = 0; x < TEX; x++) {
      const s1 = px(Math.round(x + sx * LIT_OFFSET), Math.round(y + sz * LIT_OFFSET));
      const s2 = px(Math.round(x + sx * LIT_OFFSET * 2), Math.round(y + sz * LIT_OFFSET * 2));
      // ⚠️ Множитель 0.6, а не «в ноль»: облако снизу подсвечено землёй и
      // рассеянным светом всего неба, и полностью тёмная сторона читается не
      // как объём, а как грязное пятно.
      const lit = 1 - Math.min(1, s1 * 0.6 + s2 * 0.4) * 0.6;
      const i = (y * TEX + x) * 4;
      data[i] = dens[y * TEX + x] * 255;
      data[i + 1] = lit * 255;
      data[i + 2] = 0;
      data[i + 3] = 255;
    }
  }

  const t = new THREE.DataTexture(data, TEX, TEX, THREE.RGBAFormat);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter;
  // ⚠️ Мипы обязательны: у горизонта тайл сжимается в несколько пикселей, и без
  // них там кипит муар — самое заметное, что вообще может делать небо.
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.anisotropy = 4;
  t.needsUpdate = true;
  return t;
}

const VERT = /* glsl */`
varying vec3 vDir;
void main() {
  // Направление взгляда в МИРОВЫХ координатах: по нему считается и солнце,
  // и проекция слоя облаков, поэтому поворот самого купола ни на что не влияет.
  vDir = (modelMatrix * vec4(position, 1.0)).xyz - cameraPosition;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */`
uniform vec3 uZenith, uHorizon, uSunDir, uSunTint, uCloudLit, uCloudDark;
uniform sampler2D uClouds;
uniform vec2 uWind;
uniform float uTime, uScale, uDiskGain;
varying vec3 vDir;

void main() {
  vec3 d = normalize(vDir);

  // 1) градиент: у горизонта — ровно цвет тумана из scene.js, чтобы дальние
  //    стены и край пола растворялись в небе без видимой границы.
  vec3 col = mix(uHorizon, uZenith, pow(clamp(d.y, 0.0, 1.0), 0.55));

  // 2) солнце: широкое рассеяние + плотный ореол + диск.
  float sd = max(dot(d, uSunDir), 0.0);
  col += uSunTint * (pow(sd, 6.0) * 0.10 + pow(sd, 220.0) * 0.9);
  col += uSunTint * uDiskGain * smoothstep(${DISK_OUT.toFixed(5)}, ${DISK_IN.toFixed(5)}, sd);

  // 3) облака — ПОСЛЕ солнца: облако обязано его закрывать, а не наоборот.
  //    Проекция взгляда на горизонтальный слой над головой; деление на d.y и
  //    даёт перспективу (к горизонту облака сжимаются).
  //    ⚠️ Ветки "if (fade > 0)" здесь нет намеренно: выборка текстуры внутри
  //    условия ломает вычисление производных, по которым GPU выбирает мип.
  //    (И кавычки тут двойные, а не обратные: текст шейдера сам лежит в
  //    шаблонной строке JS, обратная кавычка закрыла бы её посреди кода.)
  //    Полоса у самого горизонта отдана дымке намеренно: там слой облаков виден
  //    почти с ребра, тайл сжимается в считанные пиксели и вместо облаков
  //    получается кипящая рябь.
  float fade = smoothstep(0.06, 0.34, d.y);
  vec2 uv = d.xz / max(d.y, 0.05) * uScale + uWind * uTime;
  vec2 c = texture2D(uClouds, uv).rg;
  col = mix(col, mix(uCloudDark, uCloudLit, c.g), c.r * fade);

  gl_FragColor = vec4(col, 1.0);
}
`;

/**
 * Собрать небесный купол.
 *
 * @param {THREE.Vector3} sunDir единичное направление НА солнце — то же, из
 *        которого светит DirectionalLight сцены. ⚠️ Разъедутся — диск на небе
 *        перестанет совпадать с тем, откуда падают тени, и это будет
 *        единственный способ заметить рассинхрон.
 * @param {{horizon:number, zenith:number, sun:number}} colors
 * @returns {THREE.Mesh}
 */
export function makeSky(sunDir, colors) {
  const mat = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    side: THREE.BackSide,
    depthWrite: false,       // купол не должен мешать ничему, что нарисуется после
    fog: false,              // он сам себе даль: горизонт покрашен цветом тумана
    uniforms: {
      uZenith: { value: new THREE.Color(colors.zenith) },
      uHorizon: { value: new THREE.Color(colors.horizon) },
      uSunTint: { value: new THREE.Color(colors.sun) },
      uSunDir: { value: sunDir.clone().normalize() },
      uCloudLit: { value: new THREE.Color(0xffffff) },
      uCloudDark: { value: new THREE.Color(0x9fb0c4) },
      uClouds: { value: makeCloudTexture(sunDir) },
      uWind: { value: new THREE.Vector2(WIND[0], WIND[1]) },
      uScale: { value: CLOUD_SCALE },
      uDiskGain: { value: DISK_GAIN },
      uTime: { value: 0 },
    },
  });

  const sky = new THREE.Mesh(new THREE.SphereGeometry(300, 48, 24), mat);
  sky.frustumCulled = false;       // купол вокруг камеры — отсекать его нечем
  sky.castShadow = sky.receiveShadow = false;
  // ⚠️ Рисуем в самом конце непрозрачной очереди: к этому моменту глубина карты
  // уже записана, и небо шейдится только в реально видимых просветах.
  sky.renderOrder = 1000;

  // ⚠️ Перенос купола в камеру и часы ветра — здесь, а не в главном цикле:
  // модуль обязан быть самодостаточным, иначе «небо поехало» после любой
  // правки main.js. `updateMatrixWorld` обязателен — three считает
  // modelViewMatrix сразу ПОСЛЕ этого колбэка, но матрицу мира сам уже не
  // пересобирает.
  sky.onBeforeRender = (renderer, scene, cam) => {
    cam.getWorldPosition(sky.position);
    sky.updateMatrixWorld();
    mat.uniforms.uTime.value = performance.now() / 1000;
  };

  return sky;
}
